import { Link, useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { buttonVariants } from "@/components/ui/button";
import { Toolbar, cn } from "@jbnl/shared-ui";
import { Skeleton, SkeletonLine } from "@/components/ui/skeleton";
import { InschrijfperiodeKiezer } from "@/features/inschrijving/InschrijfperiodeKiezer";
import { huidigePeriode, vensterStatus } from "@/features/inschrijving/venster";
import { PloegenTabel, PloegenTabelSkeleton } from "./PloegenTabel";
import {
  useInschrijving,
  useKorpsPeriodes,
  type Inschrijving,
} from "./useInschrijving";
import type { components } from "@jbnl/api-client";

type KorpsOption = components["schemas"]["KorpsOption"];

/** The Link is disabled instead of hidden, so the entry point stays put once a window shuts. */
function PloegenOverzicht({
  korpsId,
  periodeId,
  venster,
  inschrijving,
  onKiesPloeg,
}: {
  korpsId: string;
  periodeId: string | undefined;
  venster: ReturnType<typeof vensterStatus>;
  inschrijving: Inschrijving;
  onKiesPloeg: (ploegId: string) => void;
}) {
  const { t } = useTranslation("inschrijving");

  return (
    <>
      <Toolbar>
        {/* A link, not a button: it is a destination, so middle-click works. Disabled reads as
          `aria-disabled`, which stops the click without leaving the tab order. */}
        <Link
          to="/ploeg"
          search={{ korps: korpsId, periode: periodeId }}
          aria-disabled={!venster.open}
          onClick={(event) => {
            if (!venster.open) event.preventDefault();
          }}
          className={cn(
            buttonVariants(),
            !venster.open && "pointer-events-none opacity-45",
          )}
        >
          <Plus />
          {t("lijst.inschrijven")}
        </Link>
      </Toolbar>

      <PloegenTabel inschrijving={inschrijving} korpsId={korpsId} onKies={onKiesPloeg} />

      {inschrijving.ploegen.length > 0 && (
        <p className="text-sm text-muted-foreground">{t("lijst.kiesPloeg")}</p>
      )}
    </>
  );
}

/**
 * §4.2 for one korps: the ploegen it has entered under the running inschrijfperiode, and the
 * way to another. Entering and changing both happen on `/ploeg` - this screen is the list they
 * come back to.
 */
export function InschrijvingDetail({
  seizoenId,
  korps,
  named,
  periodeId,
  onKiesPeriode,
}: {
  seizoenId: string;
  korps: KorpsOption;
  /** Off where the korps was never in doubt - the page title already says whose ploegen these are. */
  named: boolean;
  /** The window from the URL, so a reload and a shared link land on the one that was chosen. */
  periodeId: string | undefined;
  onKiesPeriode: (inschrijfperiodeId: string) => void;
}) {
  const { t } = useTranslation("inschrijving");
  const { data: periodes, isPending: periodesPending } =
    useKorpsPeriodes(seizoenId);
  const navigate = useNavigate();

  // The open one to start with, otherwise the first there is. No read is ever gated, so a shut
  // window still draws what the korps entered under it (ADR-0031).
  const periode =
    periodes?.find((kandidaat) => kandidaat.id === periodeId) ??
    huidigePeriode(periodes);
  const venster = vensterStatus(periode);

  const {
    data: inschrijving,
    isPending,
    isError,
  } = useInschrijving(periode?.id, korps.id);

  return (
    <div className="space-y-4">
      {named && (
        <div>
          <h2 className="text-lg font-bold tracking-[-0.02em]">{korps.name}</h2>
          <p className="text-sm text-muted-foreground">{korps.city}</p>
        </div>
      )}

      {/* Only where there is something to choose: most seizoenen give a korps one window, and a
        picker offering one option is a control to read past. */}
      {periodes && periodes.length > 1 && (
        <InschrijfperiodeKiezer
          id={`ploegen-periode-${korps.id}`}
          periodes={periodes}
          gekozen={periode?.id}
          onKies={onKiesPeriode}
        />
      )}

      {/* A bar while the periodes load: the sentence underneath is an answer, and showing it
        during the wait told every korps the window was shut. */}
      {periodesPending ? (
        <SkeletonLine className="w-72" />
      ) : (
        <p className="text-sm text-muted-foreground">
          {periode
            ? t("venster.metPeriode", {
                periode: periode.name,
                venster: venster.label,
              })
            : venster.label}
        </p>
      )}

      {inschrijving && (
        <PloegenOverzicht
          korpsId={korps.id}
          periodeId={periode?.id}
          venster={venster}
          inschrijving={inschrijving}
          onKiesPloeg={(ploegId) =>
            void navigate({
              to: "/ploeg",
              search: { korps: korps.id, ploeg: ploegId, periode: periode?.id },
            })
          }
        />
      )}

      {periode && isPending && (
        <>
          <Toolbar>
            <Skeleton className="h-9 w-44" />
          </Toolbar>
          <PloegenTabelSkeleton />
        </>
      )}

      {isError && (
        <p className="text-sm text-destructive">{t("errors.ophalen")}</p>
      )}
    </div>
  );
}
